import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { MatDialog } from '@angular/material/dialog';
import { LevelSuccessDialogComponent } from '../component/dialogs/level-success-dialog/level-success-dialog.component';
import { TimerControlService } from './timer-control.service';
import { ChatMessage } from '../model/chat-message.interface';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { Route } from '../enum/route.enum';
import { RouterService } from './router.service';
import { GameThemeService } from './game-theme.service';
import { Language } from '../enum/language.enum';

interface DialogData {
  badgeName: string[] | null;
  currentLevel: number | null;
  nextLevel: number | null;
  userLevelDuration: number | null;
  userLevelAttempts: number | null;
  userTotalScore: { score: string; total_attempts: number; total_pings: number } | null;
}

interface AiAgentResponse {
  body: {
    ai_response: string;
    game_character?: string;
    status?: boolean;
    validation_error?: string;
    level_complete?: boolean;
    badgeName?: string[];
    currentLevel?: number;
    nextLevel?: number;
    userLevelScore?: string;
    userLevelDuration?: number;
    userLevelAttempts?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AiAgentService {
  public response = signal<string>('');
  public gameCharacter = signal<string>('');
  public status = signal<boolean>(false);
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);
  public validationError = signal<string | null>(null);
  public showNextLevelButton = signal<boolean>(false);
  public showFinishGameButton = signal<boolean>(false);
  public showFinishGameAnimation = signal<boolean>(false);
  public pendingSequentialMessages = signal<ChatMessage[] | null>(null);

  public hideInputField = signal<boolean>(false);
  public showHintsSection = signal<boolean>(true);
  private readonly http = inject(HttpClient);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly timerControlService = inject(TimerControlService);
  private readonly routerService = inject(RouterService);
  private readonly gameThemeService = inject(GameThemeService);
  private readonly backendApiUrl = this.gameThemeService.getBackendApiUrl();
  private readonly apiUrl = `${this.backendApiUrl}/ai_agent`;
  private dialogData: DialogData | null = null;
  private pendingSuccessLevel: number | null = null;

  /**
   * * Sends a message to the AI agent and updates the signals
   * @param message The message to be sent to the AI agent
   */
  public sendMessage(message: string): void {
    if (!message.trim()) return;

    // Add user message immediately to chat history
    this.userService.addTemporaryChatMessages([{ message, message_type: 'user' }]);

    this.isLoading.set(true);
    this.error.set(null);

    const currentLanguage = this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || Language.EN;
    const supportedLanguages = Object.values(Language);
    const userLanguage = supportedLanguages.includes(currentLanguage as Language)
      ? currentLanguage
      : Language.EN;

    const requestBody = {
      user_prompt: message,
      user_language: userLanguage,
    };

    this.http.post<AiAgentResponse>(this.apiUrl, requestBody).subscribe({
      next: (data) => {
        const body = data.body || data;

        this.response.set(body.ai_response || '');
        this.gameCharacter.set(body.game_character || '');
        this.status.set(body.status || false);

        this.userService.getUserHistory();
        this.userService.getUserAttempts();
        this.userService.getTotalScore();

        if (body.status) {
          void this.handleSuccess();
        }

        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error communicating with AI ', err);
        this.error.set('An error occurred!');
        this.isLoading.set(false);
      },
    });
  }

  public openNextLevelDialog(): void {
    // Create dialog data with current backend data when button is clicked
    const currentLevel = this.userService.userLevel();

    const levelCount = this.gameThemeService.getLevelCount();

    if (currentLevel !== null && currentLevel < levelCount) {
      // Small delay to ensure backend data is loaded before opening dialog
      setTimeout(() => {
        this.dialogData = {
          badgeName: this.userService.userAchievements(),
          currentLevel: currentLevel,
          nextLevel: currentLevel + 1,
          userTotalScore: this.userService.userTotalScore(),
          userLevelDuration: this.userService.userLevelDuration(),
          userLevelAttempts: this.userService.userAttempts(),
        };

        const dialogRef = this.dialog.open(LevelSuccessDialogComponent, {
          width: '80vw',
          minWidth: '80vw',
          disableClose: true,
          data: this.dialogData,
          panelClass: 'animateIn',
          backdropClass: 'backdropAnimateIn',
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result === true) {
            this.showHintsSection.set(true);
          }
        });

        // Hide the button and clear dialog data after opening the dialog
        this.showNextLevelButton.set(false);
        this.hideInputField.set(false);
        this.timerControlService.stopCountdown();
        this.dialogData = null;
      }, 200);
    } else if (currentLevel === levelCount) {
      this.routerService.navigateTo(Route.WINNER);
    }
  }

  public setShowFinishGameAnimation(value: boolean): void {
    this.showFinishGameAnimation.set(value);
  }

  /**
   * Gets the chat start messages for the given level as translation keys
   * Returns translation keys in the format: levels.X.chat_start.INDEX.content
   */
  public async getChatStartMessages(level: number): Promise<ChatMessage[]> {
    const key = `levels.${level}.chat_start`;
    const chatStart = await firstValueFrom(this.translateService.get(key));

    if (!Array.isArray(chatStart)) {
      return [];
    }

    const translationKeys = chatStart.map((_, index) => `levels.${level}.chat_start.${index}.content`);
    const translations = await firstValueFrom(this.translateService.get(translationKeys));

    return translationKeys.map(
      (k): ChatMessage => ({
        content: translations[k],
        message_type: 'maria',
      }),
    );
  }

  public async getChatEndMessages(level: number): Promise<ChatMessage[]> {
    const key = `levels.${level}.chat_end`;
    const chatEnd = await firstValueFrom(this.translateService.get(key));

    if (!Array.isArray(chatEnd)) {
      return [];
    }

    const translationKeys = chatEnd.map((_, index) => `levels.${level}.chat_end.${index}.content`);
    const translations = await firstValueFrom(this.translateService.get(translationKeys));

    return translationKeys.map(
      (k): ChatMessage => ({
        content: translations[k],
        message_type: 'maria',
      }),
    );
  }

  private async handleSuccess(): Promise<void> {
    const currentLevel = this.userService.userLevel();
    if (currentLevel !== null) {
      const chatEndMessages = await this.getChatEndMessages(currentLevel);
      this.pendingSuccessLevel = currentLevel;

      if (chatEndMessages && chatEndMessages.length > 0) {
        this.pendingSequentialMessages.set(chatEndMessages);
      } else {
        this.completePostSuccessFlow();
      }
    }
  }

  /**
   * Called by the chat component after sequential typewriter messages are fully rendered.
   */
  public completePostSuccessFlow(): void {
    const currentLevel = this.pendingSuccessLevel;
    this.pendingSuccessLevel = null;
    this.pendingSequentialMessages.set(null);

    if (currentLevel === null) return;

    this.hideInputField.set(true);
    this.showHintsSection.set(false);
    this.showNextLevelButton.set(true);
    this.timerControlService.stopCountdownSignal();

    if (currentLevel === this.gameThemeService.getLevelCount()) {
      this.showNextLevelButton.set(false);
      this.showFinishGameButton.set(true);
      this.showFinishGameAnimation.set(true);
    }
  }
}
